/**
 * useIssuesFilters — clientseitige Filter/Sort/Suche ueber aggregierte
 * Produkt-Issues. Bewusst clientseitig: Backend liefert workspace-weite
 * Aggregation (gebounded), Facetten/Suche/Sort sind interaktiv.
 */
import { useMemo, useState } from 'react'
import type { ProductWithIssues } from '@/lib/api/channel-readiness'
import { tally, sortProducts, toggleValue, reduceToChannel, type IssuesSort } from './useIssuesFilters.helpers'

export type { IssuesSort }

export function useIssuesFilters(products: ProductWithIssues[]) {
  const [channel, setChannel] = useState<string | null>(null)
  const [types, setTypes] = useState<Set<string>>(new Set())
  const [severities, setSeverities] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<IssuesSort>('impact_desc')

  const scoped = useMemo(() => reduceToChannel(products, channel), [products, channel])
  const typeCounts = useMemo(() => tally(scoped, 'issue_type'), [scoped])
  const severityCounts = useMemo(() => tally(scoped, 'severity'), [scoped])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const out = scoped.filter((p) => {
      if (types.size && !p.issues.some((i) => types.has(i.issue_type))) return false
      if (severities.size && !p.issues.some((i) => severities.has(i.severity ?? 'info'))) return false
      if (q && !`${p.name ?? ''} ${p.sku ?? ''}`.toLowerCase().includes(q)) return false
      return true
    })
    return sortProducts(out, sort)
  }, [scoped, types, severities, search, sort])

  return {
    filtered,
    typeCounts,
    severityCounts,
    activeTypes: types,
    activeSeverities: severities,
    activeChannel: channel,
    search,
    sort,
    setSearch,
    setSort,
    setChannel,
    toggleType: toggleValue(types, setTypes),
    toggleSeverity: toggleValue(severities, setSeverities),
  }
}
